import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import AsyncDropdown from 'src/presentation/components/forms/AsyncDropdown';
import { OpenerButton } from '@ad-scripts/presentation/components/common/dropdown_menu';
import searchDropdownResource from 'src/actions/searchDropdown/searchDropdownResource';
import useSearchDropdownAnswers from 'src/presentation/components/pages/shared/hooks/useSearchDropdownAnswers';
import useAnswersFilter, { AnswerType } from './hooks/useAnswersFilter';

type SelectAnswerDropdownProps = {
  'data-aid'?: string;
  answerType: AnswerType;
  attr?: string;
  audienceId: string;
  currentAnswerId?: string;
  className?: string;
  description?: string;
  fieldName: string;
  fitOpenerWidth?: boolean;
  knownItems?: Record<string, string>;
  label?: string;
  language: string;
  maxRows?: number;
  onAnswerIdChange: (answerId: string) => void;
  placeholder?: string;
  showEditLink?: boolean;
  text?: string;
};

export default function SelectAnswerDropdown({
  'data-aid': dataAid,
  answerType,
  attr = '',
  audienceId = '',
  currentAnswerId = '',
  className,
  description,
  fieldName,
  fitOpenerWidth = true,
  knownItems = {},
  label,
  language = '',
  maxRows = 5,
  onAnswerIdChange,
  placeholder = 'Select an Answer',
  showEditLink,
  text = '',
}: SelectAnswerDropdownProps) {
  const dispatch = useDispatch();
  const { initSearchField } = searchDropdownResource.actions;

  const answerOptions = useSearchDropdownAnswers(fieldName, language, answerType, currentAnswerId);

  useEffect(() => {
    dispatch(initSearchField({ fieldName }));
  }, [dispatch, initSearchField, fieldName]);

  const onSearchChanged = useAnswersFilter({ answerType, fieldName, language, audienceId });
  function renderLinkToEditPage() {
    if (!showEditLink) {
      return null;
    }

    return (
      currentAnswerId && (
        <StyledLink target="_blank" rel="noopener noreferrer" href={`/kb-admin/answer/${currentAnswerId}`}>
          View Answer
        </StyledLink>
      )
    );
  }

  return (
    <StyledSearchWrapper fitOpenerWidth={fitOpenerWidth} className={className}>
      <AsyncDropdown
        attr={attr}
        label={label}
        description={description}
        data-aid={dataAid}
        delay={500}
        knownItems={knownItems}
        maxRows={maxRows}
        onChange={onAnswerIdChange}
        onSearch={onSearchChanged}
        options={answerOptions}
        placeholder={placeholder}
        text={text}
        value={currentAnswerId}
      />
      {renderLinkToEditPage()}
    </StyledSearchWrapper>
  );
}

const StyledSearchWrapper = styled.div`
  position: relative;
  button {
    max-width: ${({ fitOpenerWidth, maxWidth = 268 }) => (fitOpenerWidth ? '100%' : `${maxWidth}px`)};
  }
`;

const StyledLink = styled.a`
  left: 100%;
  margin-left: 10px;
  position: absolute;
  top: 100%;
  transform: translate(0, -${p => p.theme.spacing.insetLarge});
  white-space: nowrap;
`;

export const AnswerDropdownMediumWidth = styled(SelectAnswerDropdown).attrs({ width: '250px' })`
  ${OpenerButton} {
    width: ${p => p.width};
  }

  ${StyledLink} {
    left: ${p => p.width};
  }
`;
